import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '@services/api';
import { useTranslationStore } from '@stores/translationStore';
import { promptVerifyToOrder } from '@utils/verifyToOrder';
import { emitActivityChanged } from '@utils/activityEvents';
import PageHeader from '@components/PageHeader';
import PageSpinner from '@components/PageSpinner';

// The guest scanned the kiosk's QR with their phone. Opening this page charges the purchase to their
// room — scanning the code is the consent — then shows what was charged.

interface PaidResult {
  status: string;
  orderNumber?: string;
  currencySymbol: string;
  total: number;
  items: { menuItemId: number; quantity: number; lineTotal: number }[];
  names: Record<string, string>;
}

export default function KioskPayPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslationStore();
  const [result, setResult] = useState<PaidResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  const tr = (key: string, fallback: string) => { const v = t(key); return v && v !== key ? v : fallback; };
  const hotelSlug = localStorage.getItem('gcxp_hotel_slug');

  const pay = async () => {
    setError(null);
    try {
      const res = await apiClient.post<PaidResult>(`/api/v1/${hotelSlug}/kiosk-pay/${token}`, {});
      setResult(res.data);
      emitActivityChanged();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string; code?: string } } };
      if (e.response?.data?.code === 'verify') {
        // Room charges need this phone verified first — then try again.
        promptVerifyToOrder(() => pay());
        setError(tr('kiosk_pay.verify', 'Please verify your phone number to charge your room.'));
        return;
      }
      setError(e.response?.data?.error || tr('kiosk_pay.failed', 'The charge didn’t go through. Please ask at reception.'));
    }
  };

  useEffect(() => {
    if (started.current || !token || !hotelSlug) return;
    started.current = true;
    pay();
  }, [token, hotelSlug]);

  if (!result && !error) return <PageSpinner />;

  return (
    <div style={{ padding: '1rem' }}>
      <PageHeader title={<>🛒 {tr('kiosk_pay.title', 'Kiosk purchase')}</>} />
      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: 12, padding: '1rem' }}>
          ⚠️ {error}
        </div>
      )}
      {result && (
        <div style={{ background: '#fff', borderRadius: 16, padding: '1.25rem', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
          <div style={{ textAlign: 'center', fontSize: '2.5rem' }}>✅</div>
          <h3 style={{ textAlign: 'center', margin: '0.25rem 0 1rem' }}>
            {tr('kiosk_pay.charged', 'Charged to your room')}{result.orderNumber ? ` · #${result.orderNumber}` : ''}
          </h3>
          {result.items.map(i => (
            <div key={i.menuItemId} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0' }}>
              <span>{i.quantity}× {result.names[String(i.menuItemId)] ?? ''}</span>
              <span>{i.lineTotal > 0 ? `${result.currencySymbol}${i.lineTotal.toFixed(2)}` : '—'}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, borderTop: '1px solid #E5E7EB', marginTop: 8, paddingTop: 8 }}>
            <span>{tr('kiosk_pay.total', 'Total')}</span>
            <span>{result.currencySymbol}{result.total.toFixed(2)}</span>
          </div>
          <p style={{ color: '#64748B', fontSize: 13, marginTop: 12 }}>
            {tr('kiosk_pay.notYou', 'Not you? Please contact reception.')}
          </p>
        </div>
      )}
    </div>
  );
}
